// frontend/src/pages/DataMigration.jsx
import React, { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { OrganizationService } from '../services/organizationService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

function DataMigration() {
  const { currentOrganization } = useOrganization();
  const [migrationStatus, setMigrationStatus] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  // データ存在チェック
  const checkDataExists = async () => {
    setIsChecking(true);
    addLog('データ存在チェックを開始します...', 'info');
    
    const collections = [
      'procedures',
      'skillRecords',
      'skillGroups',
      'settings',
      'shiftPatterns',
      'shifts'
    ];

    const status = {};

    try {
      for (const collectionName of collections) {
        // ルート直下のデータ数
        const rootSnap = await getDocs(collection(db, collectionName));
        const rootCount = rootSnap.size;

        // 組織配下のデータ数
        let orgCount = 0;
        if (currentOrganization?.id) {
          const orgCollection = OrganizationService.getOrgCollection(
            currentOrganization.id,
            collectionName
          );
          const orgSnap = await getDocs(orgCollection);
          orgCount = orgSnap.size;
        }

        status[collectionName] = {
          rootCount,
          orgCount,
          needsMigration: rootCount > 0 && orgCount === 0
        };

        addLog(`${collectionName}: ルート直下=${rootCount}件, 組織配下=${orgCount}件`, 
          rootCount > 0 && orgCount === 0 ? 'warning' : 'success');
      }

      setMigrationStatus(status);
      
      const needsMigration = Object.values(status).some(s => s.needsMigration);
      if (needsMigration) {
        addLog('データ移行が必要です', 'warning');
      } else {
        addLog('データ移行は不要です（すべてのデータが組織配下に存在します）', 'success');
      }
    } catch (error) {
      addLog(`エラー: ${error.message}`, 'error');
    } finally {
      setIsChecking(false);
    }
  };

  // データ移行実行
  const executeMigration = async () => {
    if (!currentOrganization?.id) {
      addLog('組織が選択されていません', 'error');
      return;
    }

    if (!window.confirm('データ移行を実行しますか？この操作には時間がかかる場合があります。')) {
      return;
    }

    setIsMigrating(true);
    addLog('=== データ移行を開始します ===', 'info');

    const collections = [
      'departments',
      'procedures',
      'staffList',
      'skillRecords',
      'skillGroups',
      'shiftPatterns',
      'shifts',
      'surgeries'
    ];

    try {
      for (const collectionName of collections) {
        setCurrentStep(`${collectionName} を移行中...`);
        addLog(`${collectionName} の移行を開始...`, 'info');

        const result = await OrganizationService.migrateExistingDataToOrganization(
          currentOrganization.id,
          collectionName
        );

        if (result.success) {
          addLog(`✅ ${collectionName}: ${result.migratedCount}件を移行しました`, 'success');
        } else {
          addLog(`❌ ${collectionName}: 移行失敗 - ${result.error}`, 'error');
        }

        // APIレート制限対策
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      addLog('=== データ移行が完了しました ===', 'success');
      addLog('ページをリロードして変更を確認してください', 'info');
      
      // 移行完了フラグを設定
      localStorage.setItem(`migration_completed_${currentOrganization.id}`, 'true');
      
    } catch (error) {
      addLog(`移行エラー: ${error.message}`, 'error');
    } finally {
      setIsMigrating(false);
      setCurrentStep('');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '1rem'
          }}>
            🔄 データ移行ツール
          </h1>
          
          <p style={{
            color: '#6b7280',
            marginBottom: '2rem'
          }}>
            既存のデータを組織ベースの構造に移行します。<br/>
            現在の組織: <strong>{currentOrganization?.name || '未選択'}</strong>
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <button
              onClick={checkDataExists}
              disabled={isChecking || isMigrating}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: isChecking ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isChecking || isMigrating ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isChecking ? '確認中...' : '📊 データ状態を確認'}
            </button>

            <button
              onClick={executeMigration}
              disabled={isMigrating || isChecking || !currentOrganization?.id}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: isMigrating ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isMigrating || isChecking || !currentOrganization?.id ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {isMigrating ? '移行中...' : '🚀 データ移行を実行'}
            </button>
          </div>

          {currentStep && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#dbeafe',
              borderLeft: '4px solid #3b82f6',
              borderRadius: '4px',
              marginBottom: '1rem'
            }}>
              <p style={{ color: '#1e40af', fontWeight: '500' }}>
                {currentStep}
              </p>
            </div>
          )}
        </div>

        {/* データ状態表示 */}
        {Object.keys(migrationStatus).length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem'
            }}>
              データ状態
            </h2>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      コレクション
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      ルート直下
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      組織配下
                    </th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                      状態
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(migrationStatus).map(([name, status]) => (
                    <tr key={name} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                        {name}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {status.rootCount}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {status.orgCount}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {status.needsMigration ? (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}>
                            要移行
                          </span>
                        ) : status.orgCount > 0 ? (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}>
                            完了
                          </span>
                        ) : (
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            borderRadius: '12px',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}>
                            データなし
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ログ表示 */}
        {logs.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#1f2937',
              marginBottom: '1rem'
            }}>
              実行ログ
            </h2>
            
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              backgroundColor: '#1f2937',
              borderRadius: '8px',
              padding: '1rem',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              {logs.map((log, index) => (
                <div
                  key={index}
                  style={{
                    padding: '0.25rem 0',
                    color: log.type === 'error' ? '#fca5a5' :
                           log.type === 'warning' ? '#fcd34d' :
                           log.type === 'success' ? '#86efac' : '#d1d5db'
                  }}
                >
                  [{log.timestamp}] {log.message}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 注意事項 */}
        <div style={{
          marginTop: '2rem',
          padding: '1.5rem',
          backgroundColor: '#fef3c7',
          borderLeft: '4px solid #f59e0b',
          borderRadius: '8px'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: '#92400e',
            marginBottom: '0.5rem'
          }}>
            ⚠️ 注意事項
          </h3>
          <ul style={{
            color: '#78350f',
            paddingLeft: '1.5rem',
            margin: 0
          }}>
            <li>データ移行は元のデータを削除せず、組織配下にコピーします</li>
            <li>移行後も元のデータはルート直下に残ります</li>
            <li>移行には時間がかかる場合があります（データ量による）</li>
            <li>移行中はブラウザを閉じないでください</li>
            <li>移行完了後、ページをリロードして正しくデータが表示されることを確認してください</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default DataMigration;